import { ApplyChartTemplateToDocMgr } from "../utils/ApplyChartTemplateToDocMgr";
import { appstr } from "../utils/appstr";
import { enumChartTemplateSet, enumHierarchyTypes } from "../constants";
import { QChartTemplate } from "./QChartTemplate";
import { Globals } from "./Globals";
import { doApplyChartTemplate } from "../utils/doApplyChartTemplate.js";

/**
 * @typedef {import('./QChartTemplateRule').QChartTemplateRule} QChartTemplateRule
 */

/**
 * @typedef {Object} TemplateInfo
 * @property {string} name
 * @property {string} id
 * @property {number} templateSet - enumChartTemplateSet value
 * @property {number} hierarchyType - enumHierarchyTypes value
 */

/**
 * @typedef {Object} TemplateRuleInfo
 * @property {string} name
 * @property {string} id
 * @property {number} templateSet - enumChartTemplateSet value
 * @property {QChartTemplateRule[]} rules
 */

export class QChartTemplatesMgr {
  constructor() {
    this.fSysTemplates = null;
    this.fSharedTemplates = null;
    this.fUserTemplates = null;

    this.fTemplateList = null;
    this.fTemplateSet = enumChartTemplateSet.sharedtemplates;

    this.fDirty = { 1: false, 2: false };
    this.fWSMgr = null;
  }

  InitChartTemplatesMgr(wsMgr) {
    this.fWSMgr = wsMgr;
    this.fSysTemplates = [];
    this.fSharedTemplates = [];
    this.fUserTemplates = [];

    this.fTemplateList = null;
  }

  GetDoc() {
    var theDoc = this.fWSMgr.GetDoc();
    return theDoc;
  }

  SwitchTemplates(templateSet) {
    this.fTemplateSet = templateSet;
    switch (this.fTemplateSet) {
      case enumChartTemplateSet.sharedtemplates:
        this.fTemplateList = this.fSharedTemplates;
        break;
      case enumChartTemplateSet.usertemplates:
        this.fTemplateList = this.fUserTemplates;
        break;
      default:
        this.fTemplateList = this.fSysTemplates;
    }
    if (Globals._wsMgr.fViewContextProviderFn)
      Globals._wsMgr.fViewContextProviderFn({ state: this });
  }

  SwitchTemplatesWithAccessGroup(templateSet, docParam) {
    // Default is to pull curDoc from wsMgr.
    var wsMgr = this.fWSMgr;
    this.SwitchTemplates(templateSet);
    if (templateSet != enumChartTemplateSet.sharedtemplates)
      // Only shared templates are gated.
      return;
    var curDoc = docParam == null ? wsMgr.GetDoc() : docParam;
    this.fTemplateList = this.GetTemplateList(this.fTemplateList, curDoc);
  }

  GetAllowedViewsFromViewMgr(curDoc) {
    var viewMgr = curDoc != null ? curDoc.GetViewMgr() : null;
    var allowedViews = viewMgr ? viewMgr.GetAllowedViewList() : null;

    return allowedViews;
  }

  CurrentTemplateSet() {
    return this.fTemplateSet;
  }

  CurrentTemplateSetStr() {
    switch (this.fTemplateSet) {
      case enumChartTemplateSet.sharedtemplates:
        return appstr("Shared");
      case enumChartTemplateSet.usertemplates:
        return appstr("User");
      default:
        return appstr("System");
    }
  }

  SetDirty() {
    this.fDirty[this.fTemplateSet] = true;
  }

  ChartTemplatesDirty() {
    if (this.fDirty[this.fTemplateSet]) return true;

    for (var aTemplateIdx in this.fTemplateList) {
      var theTemplate = this.fTemplateList[aTemplateIdx];
      if (theTemplate.IsDirty()) return true;
    }
    return false;
  }

  ClearDirty() {
    this.fDirty[this.fTemplateSet] = false;
    //TODO: Handle correct library here
    for (var aTemplateIdx in this.fTemplateList) {
      var theTemplate = this.fTemplateList[aTemplateIdx];
      theTemplate.SetDirty(false);
    }
  }

  UpdateContext() {
    Globals._wsMgr.fViewContextProviderFn({ state: this });
  }

  ChartTemplatesSaved(savedTemplate) {
    if (savedTemplate) {
      savedTemplate.SetDirty(false);
    } else {
      this.ClearDirty();
    }
    if (Globals._wsMgr.fViewContextProviderFn)
      Globals._wsMgr.fViewContextProviderFn({ state: this });
  }

  GetNumTemplates() {
    return this.fTemplateList.length;
  }

  GetIndexTemplate(index) {
    return this.fTemplateList[index];
  }

  GetIndexTemplateName(index) {
    var aTemp = this.fTemplateList[index];
    return aTemp.GetTemplateName();
  }

  GetTemplateNameList() {
    var n = this.GetNumTemplates();
    var nameList = [];

    for (var i = 0; i < n; i++) {
      var iT = this.GetIndexTemplate(i);
      var tName = iT.GetTemplateName();
      nameList.push(tName);
    }

    return nameList;
  }

  GetCompleteTemplateNameList(templateSet) {
    var templatesArray;
    if (templateSet != null) templatesArray = this.GetTemplateList(templateSet);
    else {
      templatesArray = [];
      this.ForEachTemplateInAllSets(function (t) {
        templatesArray.push(t);
      });
    }
    var templateNames = templatesArray.map(function (aTemplate) {
      return aTemplate.GetTemplateName();
    });
    var templateIDs = templatesArray.map(function (aTemplate) {
      return aTemplate.GetTemplateID();
    });
    var uniques = new Set(templateNames.concat(templateIDs));
    return Array.from(uniques).filter(function (anIdentifier) {
      return anIdentifier != null;
    });
  }

  FindTemplateByNameAllSets(templateName, suppressFallback) {
    //SAFE way to find a template! Searches all lists, prioritizing userlist, orglist, then system list.
    //Safe fallback.
    //Will add personal template
    var theSearchArrays = [
      this.GetTemplateList(enumChartTemplateSet.usertemplates),
      this.GetTemplateList(enumChartTemplateSet.sharedtemplates),
      this.GetTemplateList(enumChartTemplateSet.systemtemplates)
    ];

    for (var aSearchListIdx in theSearchArrays) {
      var theArrayToSearch = theSearchArrays[aSearchListIdx];
      var theTemplate = this.FindTemplateByName(templateName, theArrayToSearch);
      if (theTemplate != null) {
        return theTemplate;
      }
    }
    if (suppressFallback) return null;
    var classicDefaultTemplate = this.GetTemplateList(enumChartTemplateSet.systemtemplates)[0];
    return classicDefaultTemplate;
  }

  FindTemplateByIDAllSets(templateID, suppressFallback) {
    //SAFE way to find a template! Searches all lists, prioritizing userlist, orglist, then system list, if none is found, will return the first system template with success false.
    var success = false;
    //Safe fallback.
    //Will add personal template
    //TODO: SuppressFallback replaces the success boolean check. We can consolidate a lot of code with that change.
    var theSearchArrays = [
      this.GetTemplateList(enumChartTemplateSet.usertemplates),
      this.GetTemplateList(enumChartTemplateSet.sharedtemplates, false),
      this.GetTemplateList(enumChartTemplateSet.systemtemplates)
    ];

    for (var aSearchListIdx in theSearchArrays) {
      var theArrayToSearch = theSearchArrays[aSearchListIdx];
      var theTemplate = this.FindTemplateByID(templateID, theArrayToSearch);
      if (theTemplate != null) {
        return { success: true, template: theTemplate };
      }
    }
    if (suppressFallback) return { success: success, template: null };
    theTemplate = this.GetTemplateList(enumChartTemplateSet.systemtemplates)[0];
    return { success: success, template: theTemplate };
  }

  FindTemplateByName(templateName, theList) {
    var n = theList.length;
    if (templateName != null) templateName = templateName.toLowerCase();
    for (var i = 0; i < n; i++) {
      var t = theList[i];
      var tName = t.GetTemplateName();
      tName = tName.toLowerCase();
      if (tName == templateName) return t;
    }
    return null;
  }

  FindTemplateByID(templateID, theList) {
    if (theList == null) theList = this.fTemplateList;
    var n = theList.length;
    for (var i = 0; i < n; i++) {
      var t = theList[i];
      var tName = t.GetTemplateID();
      if (tName == templateID) return t;
    }
    return null;
  }

  GetTemplateByName(templateName) {
    var m = this.FindTemplateByName(templateName, this.fUserTemplates);
    if (!m) m = this.FindTemplateByName(templateName, this.fSharedTemplates);
    if (!m) m = this.FindTemplateByName(templateName, this.fSysTemplates);

    return m;
  }

  GetTemplateIDList(theList, curDoc) {
    var templates = this.GetTemplateList(theList, curDoc) || [];
    return templates.map(function (template) {
      return template.GetTemplateID();
    });
  }

  GetTemplateList(theList, curDoc) {
    var templatesToReturn = this.fTemplateList; // Default return is the template list.
    switch (theList) {
      case enumChartTemplateSet.systemtemplates:
        templatesToReturn = this.fSysTemplates;
        break;
      case enumChartTemplateSet.sharedtemplates:
        templatesToReturn = this.fSharedTemplates;
        break;
      case enumChartTemplateSet.usertemplates:
        templatesToReturn = this.fUserTemplates;
        break;
    }

    var internalTemplateSet = this.CurrentTemplateSet();
    /* Cases where there aren't any additional filters.
       1. System Templates and User Templates are always accessible. Though, that's not ideal.
       2. When curDoc is explicitly false (caller opts out of filtering).
       3. For full access group users.
    */
    var isAccessibleSet =
      [enumChartTemplateSet.systemtemplates, enumChartTemplateSet.usertemplates].includes(
        theList
      ) ||
      (internalTemplateSet != null && internalTemplateSet != enumChartTemplateSet.sharedtemplates);
    var skipFiltering = curDoc === false;
    var isFullAccess = this.fWSMgr.GetUserGroupID() == "0";
    if (isAccessibleSet || skipFiltering || isFullAccess) return templatesToReturn;

    /*	curDoc value -> expected behavior
      false -> Returned (ABOVE) all templates regardless of visibility.
      null/undefined/[default] -> Returns all visible templates.
      QXDoc -> Returns only visible templates allowed for the given doc's chart.
     */
    if (curDoc != null) {
      var allowedViews = this.GetAllowedViewsFromViewMgr(curDoc);
      if (allowedViews != null)
        templatesToReturn = templatesToReturn.filter(function (template) {
          return allowedViews.includes(template.GetTemplateID());
        });
    }

    return templatesToReturn;
  }

  GetSystemDefaultTemplate() {
    return this.fSysTemplates[0];
  }

  LoadLegacyUserTemplateSet(theList) {
    this.fUserTemplates = [];
    return;
    /*
    var usertemplates = [];

    for (var aTemplateIdx in theList)
    {
      var theTemplate = theList[aTemplateIdx];
      var theNewTemplate = new InitTemplate();
      theNewTemplate.InitTemplate();
      theNewTemplate.fTemplateID = theTemplate.fTemplateName;
      theNewTemplate.fTemplateName = theTemplate.fTemplateName;
      for (var aRuleIdx in theTemplate.fRuleList)
      {
        var theRule = theTemplate.fRuleList[aRuleIdx];
        var aNewRule = new QChartTemplateRule();
        if (theRule.fNoRule)
          aNewRule.SetRuleCriteria('Always Apply');
        else if (theRule.fComplexRule)
        {
          aNewRule.SetRuleCriteria('Complex Rule');
          aNewRule.SetComplexDef(aNewRule.SetComplexDef);
        }
        for (var aProp in theRule)
        {
          if (aProp == 'fRulePairList')
          {
            var newRulesList = [];
            for (var aRulePair in theRule.fRulePairList)
            {
              var newRulePair = new QRulePair();
              for (var aProp in theRule.fRulePairList[aRulePair])
                newRulePair[aProp] = theRule.fRulePairList[aRulePair][aProp];

              newRulesList.push(newRulePair)
            }
            aNewRule.fRulePairList = newRulesList;
          }
          else
            aNewRule[aProp] = theRule[aProp];
        }
        theNewTemplate.fRuleList.push(aNewRule);
      }
      usertemplates.push(theNewTemplate)

    }
    */
  }

  LoadTemplateSet(templateList, theList) {
    var numTemplates = templateList.length; // objTree.GetNumChildrenOfType("qcharttemplate");
    var isShared = theList == this.fSharedTemplates;
    var wsMgr = this.fWSMgr;
    var userGroupID = wsMgr.GetUserInfo().GetUserGroupID();
    var accessGroupMgr = wsMgr.GetAccessGroupMgr();
    var allowedViews = new Set();

    if (isShared) {
      // Only flag shared templates.
      var userGroup = accessGroupMgr.GetGroupByID(userGroupID);
      var applicableChartIDs =
        userGroup != null ? userGroup.GetApplicableChartIDs("allowedViews") : [];
      for (var chartId of applicableChartIDs)
        for (var allowedView of userGroup.GetAllowedViewsFromChartID(chartId))
          allowedViews.add(allowedView);
    }

    for (var i = 0; i < numTemplates; i++) {
      var templateInfo = templateList[i]; // objTree.GetIndexChildOfType("qcharttemplate",i);
      if (isShared)
        templateInfo["visible"] = userGroupID == "0" || allowedViews.has(templateInfo["ID"]); // Charts are always visible to the full access group.
      var aTemplate = new QChartTemplate();
      aTemplate.InitTemplate();
      aTemplate.LoadFromJSON(templateInfo);
      if (aTemplate.GetTemplateID() != null && aTemplate.GetTemplateName() != null)
        theList.push(aTemplate);
      else console.log("null template Name/ID");
    }
  }

  LoadFromJSON(theData, clear) {
    // We are loading both the system and shared templates
    // But saving only the shared templates
    if (theData["system"]) {
      if (clear) this.fSysTemplates = [];
      this.LoadTemplateSet(theData["system"], this.fSysTemplates);
    }
    if (theData["shared"]) {
      if (clear) this.fSharedTemplates = [];
      this.LoadTemplateSet(theData["shared"], this.fSharedTemplates);
    }
    // if (theData['user'])
    // {
    // 	if (clear)
    // 		this.fSharedTemplates = [];
    // 	this.LoadTemplateSet(theData['shared'], this.fSharedTemplates);
    // }
  }

  UsesBoxStyle(boxStyleIDList) {
    var curSet = this.CurrentTemplateSet();
    this.SwitchTemplates(enumChartTemplateSet.sharedtemplates);

    var n = this.GetNumTemplates();
    var m = boxStyleIDList.length;

    for (var i = 0; i < n; i++) {
      var t = this.GetIndexTemplate(i);
      for (var j = 0; j < m; j++) {
        var boxStyleID = boxStyleIDList[j];
        if (t.UsesBoxStyle(boxStyleID)) {
          this.SwitchTemplates(curSet);
          return true;
        }
      }
    }

    this.SwitchTemplates(curSet);
    return false;
  }

  // Helper for iterating over each template in all template sets
  /**
   * @param {function(QChartTemplate, number): void} callback
   * @param {QXDoc|false} [curDoc] - The doc to filter allowed views by, or false to skip filtering.
   */
  ForEachTemplateInAllSets(callback, curDoc) {
    var setKeys = [
      enumChartTemplateSet.systemtemplates,
      enumChartTemplateSet.sharedtemplates,
      enumChartTemplateSet.usertemplates
    ];

    for (var s = 0; s < setKeys.length; s++) {
      var templates = this.GetTemplateList(setKeys[s], curDoc);
      for (var i = 0; i < templates.length; i++) {
        callback(templates[i], setKeys[s]);
      }
    }
  }

  /**
   * @returns {Map<string, number>}
   */
  GetBoxStyleUsageCounts() {
    var counts = new Map();
    this.ForEachTemplateInAllSets(function (t) {
      t.GetReferencedBoxStyleIDs().forEach(function (id) {
        counts.set(id, (counts.get(id) || 0) + 1);
      });
    }, false);
    return counts;
  }

  /**
   * @param {string} boxStyleID
   * @returns {TemplateInfo[]}
   */
  GetTemplatesForBoxStyle(boxStyleID) {
    var results = [];
    var curDoc = this.GetDoc();
    var docHierarchyType =
      curDoc && curDoc.IsPositionDocument()
        ? enumHierarchyTypes.positions
        : enumHierarchyTypes.employees;
    this.ForEachTemplateInAllSets(function (t, templateSet) {
      if (
        t.GetReferencedBoxStyleIDs().has(boxStyleID) &&
        t.GetHierarchyType() === docHierarchyType
      ) {
        results.push({
          name: t.GetTemplateName(),
          id: t.GetTemplateID(),
          templateSet: templateSet,
          hierarchyType: t.GetHierarchyType()
        });
      }
    }, curDoc);
    return results;
  }

  GetIndexOfTemplate(aTemplate) {
    var index = this.fTemplateList.indexOf(aTemplate);
    return index;
  }

  ShuffleTemplate(aTemplate, shuffleUp) {
    var index = this.GetIndexOfTemplate(aTemplate);
    var n = this.GetNumTemplates();
    var newIndex;

    if (shuffleUp) newIndex = index - 1;
    else newIndex = index + 1;

    if (newIndex < 0) return;
    if (newIndex >= n) return;

    var t = this.fTemplateList[newIndex];
    this.fTemplateList[newIndex] = aTemplate;
    this.fTemplateList[index] = t;
    this.SetDirty();
  }

  RemoveTemplate(aTemplate) {
    var index = this.GetIndexOfTemplate(aTemplate);
    if (index != -1) this.fTemplateList.splice(index, 1);
    this.SetDirty();
  }

  ReplaceTemplate(origTemplate, newTemplate) {
    var isDirty = true;
    //TODO: Why are we not using a dedicated add template fxn?
    //Also, no validation checks in here to ensure validity?
    if (origTemplate == null) this.fTemplateList.push(newTemplate);
    // no need to do name mangling here as long as there is no case where a user is permitted to view a subset of all templates.
    else {
      isDirty = !origTemplate.TemplatesMatch(newTemplate);
      if (isDirty) {
        var index = this.GetIndexOfTemplate(origTemplate);
        if (index != -1) this.fTemplateList[index] = newTemplate;
      }
    }

    if (isDirty) {
      this.SetDirty();
      this.SyncTemplateSet();
    }
  }

  SyncTemplateSet() {
    if (this.CurrentTemplateSet() == enumChartTemplateSet.sharedtemplates) {
      this.fSharedTemplates = [];
      for (var aTemplateIdx in this.fTemplateList) {
        var theTemplate = this.fTemplateList[aTemplateIdx];
        this.fSharedTemplates.push(theTemplate);
      }
    } else if (this.CurrentTemplateSet() == enumChartTemplateSet.usertemplates) {
      this.fUserTemplates = [];
      for (var aTemplateIdx in this.fTemplateList) {
        var theTemplate = this.fTemplateList[aTemplateIdx];
        this.fUserTemplates.push(theTemplate);
      }
    }
  }

  IsLastTemplate(aTemplate) {
    var index = this.GetIndexOfTemplate(aTemplate);
    if (index != -1 && index == this.GetNumTemplates() - 1) return true;

    return false;
  }

  IsFirstTemplate(aTemplate) {
    var index = this.GetIndexOfTemplate(aTemplate);
    if (index == 0) return true;

    return false;
  }

  GetUniqueName(theName = appstr("Untitled")) {
    var nameCounter = 1;
    var tryName = theName;
    while (
      this.FindTemplateByNameAllSets(tryName, true) ||
      this.FindTemplateByIDAllSets(tryName, true)["success"]
    ) {
      tryName = theName + nameCounter.toString();
      nameCounter++;
    }
    return tryName;
  }

  GetUniqueID() {
    //TODO: We should cut to a UUID system with this...potential naughty ID overlaps.
    var n = 0;
    var tID,
      done = false;

    while (!done) {
      n++;
      tID = n.toString();
      var findNameResult = this.FindTemplateByNameAllSets(tID, true);
      var findIDResult = this.FindTemplateByIDAllSets(tID, false);
      done = findNameResult == null && findIDResult["success"] === false;
    }

    return tID;
  }

  IsNameUnique(templateName, excludeName) {
    var existingTemplate = this.FindTemplateByNameAllSets(templateName, true);

    return !existingTemplate || existingTemplate.GetTemplateName() === excludeName;
  }

  ChartBoxCount() {
    var w = this.fWSMgr;
    var theDoc = w.GetDoc();
    if (!theDoc) return 0;
    var scm = theDoc.GetSubChartMgr();
    var n = scm.GetSCNodeCount();

    return n;
  }

  NeedProgressMeter() {
    var n = this.ChartBoxCount();
    return n > 500;
  }

  DoApplyChartTemplate(theTemplate) {
    if (this.NeedProgressMeter()) this.fWSMgr.DoProgress(appstr("applyingtemplate"));
    Globals._ctpRef = this;
    Globals._ctpRef["theTemplate"] = theTemplate;
    setTimeout(function () {
      doApplyChartTemplate();
    }, 0);
  }

  ApplyChartTemplate(theTemplate) {
    // Apply Chart Template to a multi-doc chart
    var w = this.fWSMgr;
    var applyTemplate = true;

    var targetDoc = w.GetDoc();
    if (targetDoc == null) applyTemplate = false;
    else {
      var boxList = w.GetTargetTrees(false, targetDoc);
      if (boxList == null) applyTemplate = false;
    }

    if (applyTemplate) {
      ApplyChartTemplateToDocMgr(
        theTemplate,
        this.fWSMgr,
        w.GetBoxStylesMgr(),
        targetDoc,
        this["doopt"]
      );
      var dm = w.GetDocMgr();
      if (!Globals._accountInfo.ReadOnlyMode()) dm.SetDirty(true);
      w.ClearUndoRedo();
    }

    w.EndProgress();
    w.PrepRenderWS();
    w.RenderWS();
  }
}
